/**
 * A drill shared into a post, watched where it was shared.
 *
 * Tapping a drill in the feed used to leave Community for the library, which
 * is a long way to go to see a 20 second clip, and the way back is the back
 * button and a feed scrolled to the top. Now it plays over the feed and
 * closing puts the coach back on the post they were reading.
 *
 * The library is still one tap away, because that is where the drill can be
 * saved, written on and added to a list.
 *
 * The embed carries no playback id (see DrillEmbed for why), so the drill is
 * read by id here, as the signed-in coach, through the same visibility the
 * library uses. Anything that isn't plainly watchable (gone, teased, no
 * video) goes to the library instead, which already knows how to say so.
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X, ArrowRight } from "lucide-react";
import Sheet from "../shared/Sheet";
import MuxPlayer from "../components/LazyMuxPlayer";
import { GOLD } from "../constants/theme";
import { sectionLabel } from "../constants/navigation";
import { fetchDrillById } from "../hooks/useDrills";
import { logEvent } from "../utils/analytics";
import { shownKeyPoints } from "../content/keyPoints";

/** The handoff bookmarks, search and push taps use: stash the target, then
 *  navigate to its section. ContentPage consumes it on mount. */
export function openDrillInLibrary(section, id) {
  const s = section || "pd";
  try {
    sessionStorage.setItem("bam_open_drill", JSON.stringify({ sectionId: s, id }));
  } catch { /* the section landing is still the right place */ }
  window.location.hash = `#/${s}`;
}

/** Whether a fetched drill can play here rather than in the library. */
export function playsInPlace(item) {
  return !!item && !!item.muxPlaybackId && !item.comingSoon && !item.availableAt;
}

export function DrillPopup({ embed, C, onClose }) {
  const section = embed.section || "pd";
  const [item, setItem] = useState(null);

  useEffect(() => {
    let live = true;
    fetchDrillById(embed.drill_id).then(found => {
      if (!live) return;
      if (!playsInPlace(found)) {
        onClose();
        openDrillInLibrary(section, embed.drill_id);
        return;
      }
      setItem(found);
      try { logEvent("community_drill_open", { content_id: found.id, title: found.title }); } catch {}
    });
    return () => { live = false; };
  }, [embed.drill_id, section, onClose]);

  const title = item?.title || embed.title;
  const meta = [sectionLabel(section), item?.category || embed.category, item?.duration || embed.duration]
    .filter(Boolean).join(" · ");
  const points = shownKeyPoints(item);

  // On the body, not inside the post: the post's own click is a double tap to
  // like, and React bubbles through a portal, so the wrapper stops that too.
  return createPortal(
    <div onClick={ev => ev.stopPropagation()} onDoubleClick={ev => ev.stopPropagation()}>
    <Sheet C={C} onClose={onClose} label={title || "Drill"} maxWidth={760}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase",
            color: GOLD, marginBottom: 3 }}>Drill</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text, lineHeight: 1.3 }}>{title}</div>
          {meta && <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>{meta}</div>}
        </div>
        <button className="btn" onClick={onClose} aria-label="Close"
          style={{ width: 36, height: 36, borderRadius: 18, flexShrink: 0, cursor: "pointer",
            border: `1px solid ${C.border}`, background: "transparent", color: C.textMid || C.text,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
          <X size={16}/>
        </button>
      </div>

      <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: "#000",
        borderRadius: 11, overflow: "hidden", border: `1px solid ${C.border}` }}>
        {item ? (
          <MuxPlayer
            playbackId={item.muxPlaybackId}
            streamType="on-demand"
            autoPlay
            playsInline
            accentColor={GOLD}
            metadata={{ video_title: item.title }}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
        ) : embed.thumbnail_url ? (
          <div style={{ position: "absolute", inset: 0, opacity: 0.5,
            background: `#000 center/cover url("${embed.thumbnail_url}")` }}/>
        ) : null}
      </div>

      {points.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0" }}>
          {points.map((p, i) => (
            <li key={i} style={{ display: "flex", gap: 9, marginBottom: 9,
              fontSize: 13.5, lineHeight: 1.5, color: C.text }}>
              <Check size={13} color={GOLD} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 4 }}/>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
      {item?.desc && (
        <div style={{ fontSize: 13.5, lineHeight: 1.6, color: C.textDim, marginTop: points.length ? 6 : 16 }}>
          {item.desc}
        </div>
      )}

      <button className="btn" onClick={() => { onClose(); openDrillInLibrary(section, embed.drill_id); }}
        style={{ marginTop: 16, width: "100%", minHeight: 44, borderRadius: 10, cursor: "pointer",
          border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.03)", color: C.text,
          fontFamily: "inherit", fontSize: 13, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
        Open in {sectionLabel(section) || "the library"} <ArrowRight size={14}/>
      </button>
    </Sheet>
    </div>,
    document.body
  );
}

export default DrillPopup;
